import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  SUPPORTED_LOCALES,
  LOCALE_METADATA,
  loadLocaleMessages,
  type SupportedLocale,
  type LocaleMetadata,
} from '../locales';
import { i18n as globalI18n } from '../i18n';
import apiClient from '../api/client';

const STORAGE_KEY = 'user_locale';

/**
 * Sync la locale au backend (users.update_locale).
 * Best-effort : fail silently si pas authentifié ou erreur réseau.
 * La locale reste persistée en localStorage côté client de toute façon.
 */
async function syncLocaleToBackend(locale: SupportedLocale): Promise<void> {
  if (typeof localStorage === 'undefined') return;
  const token = localStorage.getItem('sf_v2_token');
  if (!token) return; // utilisateur non connecté, sync inutile

  try {
    await apiClient.post('/users.update_locale', { locale });
  } catch (err) {
    // Erreur silencieuse : la locale est déjà appliquée côté client,
    // la sync backend retentera au prochain changement.
    console.warn('[i18n] Failed to sync locale to backend:', err);
  }
}

export function useLocale() {
  // IMPORTANT : on force le scope 'global' pour que les changements de locale
  // affectent TOUTE l'app, pas seulement le composant courant. Avec legacy:false
  // (Composition mode), un useI18n() par defaut retourne une instance LOCALE
  // au composant — modifier .locale.value ne re-render que ce composant, pas
  // les autres. Resultat : le LanguageSwitcher changeait sa propre langue mais
  // pas le reste de l'app. Fix : useScope='global' + on garde une ref vers
  // i18n.global pour les setLocaleMessage afin que les chunks lazy soient
  // ajoutes a la bonne instance.
  useI18n({ useScope: 'global' });

  const currentLocale = computed(() => globalI18n.global.locale.value as SupportedLocale);

  const currentMetadata = computed<LocaleMetadata | undefined>(() =>
    LOCALE_METADATA.find((m) => m.code === currentLocale.value),
  );

  async function setLocale(locale: SupportedLocale, options: { syncBackend?: boolean } = {}) {
    const { syncBackend = true } = options;

    if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
      console.warn(`[i18n] Unsupported locale: ${locale}`);
      return;
    }

    // Charge lazily si pas deja chargee, sur l'instance GLOBAL
    if (!globalI18n.global.availableLocales.includes(locale)) {
      const messages = await loadLocaleMessages(locale);
      globalI18n.global.setLocaleMessage(locale, messages as any);
    }

    // Set la locale GLOBALE : tous les composants qui consomment via t() ou
    // useI18n({ useScope: 'global' }) re-rendent. Les composants qui ont leur
    // propre scope local (useI18n() sans option) ne sont PAS affectes — mais
    // dans Stafflow on n'a pas de scope local explicite, donc tout marche.
    globalI18n.global.locale.value = locale;

    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', locale);
    }

    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, locale);
    }

    if (syncBackend) {
      // Best-effort, non bloquant
      syncLocaleToBackend(locale);
    }
  }

  return {
    currentLocale,
    currentMetadata,
    availableLocales: LOCALE_METADATA,
    setLocale,
  };
}
